import profileIcon from "../../../assets/svg/acc.svg";
import lessonsIcon from "../../../assets/svg/material-symbols--car-tag-outline.svg";
import statsIcon from "../../../assets/svg/mdi--chart-line.svg";
import {
   getTrendBarFill,
} from "../../Common/ratingPalette";
import {
   DEFAULT_INSTRUCTOR_TYPE,
   INSTRUCTOR_TYPE_OPTIONS,
   normalizeInstructorExam,
   normalizeInstructorType,
} from "../../../constants/instructors";

const TABS = [
   { key: "profile", label: "Profil", icon: profileIcon },
   { key: "lessons", label: "Lecții", icon: lessonsIcon },
   { key: "stats", label: "Statistică", icon: statsIcon },
];

const TOKEN_HUE_MAP = {
   "--default": 60,
   "--red": 0,
   "--orange": 30,
   "--amber": 42,
   "--yellow": 56,
   "--lime": 82,
   "--green": 118,
   "--emerald": 150,
   "--teal": 175,
   "--cyan": 190,
   "--sky": 202,
   "--blue": 218,
   "--indigo": 250,
   "--violet": 270,
   "--purple": 280,
   "--fuchsia": 305,
   "--pink": 325,
};

const DEFAULT_HUE = 218;

const AVATAR_COLORS = [
   "hsl(210 90% 88%)",
   "hsl(130 65% 84%)",
   "hsl(54 95% 86%)",
   "hsl(285 100% 86%)",
   "hsl(330 96% 84%)",
   "hsl(30 100% 86%)",
];

const SLOT_OPTIONS = [
   { label: "07:00", value: "07:00" },
   { label: "08:30", value: "08:30" },
   { label: "10:00", value: "10:00" },
   { label: "11:30", value: "11:30" },
   { label: "13:30", value: "13:30" },
   { label: "15:00", value: "15:00" },
   { label: "16:30", value: "16:30" },
   { label: "18:00", value: "18:00" },
];

const WEEKDAY_OPTIONS = [
   { key: 1, label: "Lun" },
   { key: 2, label: "Mar" },
   { key: 3, label: "Mie" },
   { key: 4, label: "Joi" },
   { key: 5, label: "Vin" },
   { key: 6, label: "Sâm" },
   { key: 0, label: "Dum" },
];

const PROFILE_COLOR_PALETTE_TOKENS = [
   "--red",
   "--orange",
   "--amber",
   "--yellow",
   "--lime",
   "--green",
   "--emerald",
   "--teal",
   "--cyan",
   "--sky",
   "--blue",
   "--indigo",
   "--violet",
   "--purple",
   "--fuchsia",
   "--pink",
];

const PROFILE_COLOR_PALETTE = PROFILE_COLOR_PALETTE_TOKENS.map((token, index) => ({
   token,
   label: token,
   css: `var(--event-${token.slice(2)})`,
   visual: `hsl(${Math.round(
      (index * 360) / PROFILE_COLOR_PALETTE_TOKENS.length,
   )} 68% 58%)`,
}));

const DEFAULT_COLOR_INDEX = Math.max(
   0,
   PROFILE_COLOR_PALETTE.findIndex((color) => color.token === "--blue"),
);

const EDITABLE_INSTRUCTOR_TYPE_OPTIONS = [...INSTRUCTOR_TYPE_OPTIONS];

const firstLetter = (value) =>
   String(value || "")
      .trim()
      .charAt(0) || "";

function getInitials(instructor) {
   const fn = String(instructor?.firstName || "").trim();
   const ln = String(instructor?.lastName || "").trim();
   const a = firstLetter(fn);
   const b = firstLetter(ln);

   if (a && b) return (a + b).toUpperCase();
   return (fn.slice(0, 2) || "–").toUpperCase();
}

function hashStringToUInt(str) {
   let hash = 0;

   for (let i = 0; i < str.length; i += 1) {
      hash = (hash * 31 + str.charCodeAt(i)) | 0;
   }

   return hash >>> 0;
}

function getAvatarColorFromName(instructor) {
   const fullName =
      `${instructor?.firstName || ""} ${instructor?.lastName || ""}`.trim();

   if (!/[A-Za-z\u00C0-\u024F\u0400-\u04FF]/.test(fullName)) {
      return AVATAR_COLORS[0];
   }

   const idx =
      hashStringToUInt(fullName.normalize("NFKD")) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

function normalizeColorToCss(value, fallback) {
   const raw = String(value || "").trim();

   if (!raw) return fallback;
   if (raw.startsWith("var(")) return raw;
   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      if (!short || short === "default") return "var(--event-default)";
      if (short.startsWith("event-")) return `var(--${short})`;
      return `var(--event-${short})`;
   }
   if (/^event-/i.test(raw)) return `var(--${raw.toLowerCase()})`;
   return raw;
}

function normalizeHueToken(value) {
   const raw = String(value || "").trim();

   if (!raw) return "";
   if (TOKEN_HUE_MAP[raw] !== undefined) return raw;

   if (raw.startsWith("var(") && raw.includes("--event-")) {
      const match = raw.match(/--event-([a-z-]+)/i);
      if (match) {
         const token = `--${String(match[1]).toLowerCase()}`;
         if (TOKEN_HUE_MAP[token] !== undefined) return token;
      }
   }

   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      const token = short.startsWith("event-")
         ? `--${short.replace(/^event-/, "")}`
         : `--${short}`;
      if (TOKEN_HUE_MAP[token] !== undefined) return token;
   }

   if (/^event-/i.test(raw)) {
      const token = `--${raw.replace(/^event-/i, "").toLowerCase()}`;
      if (TOKEN_HUE_MAP[token] !== undefined) return token;
   }

   const namedToken = `--${raw.toLowerCase()}`;
   if (TOKEN_HUE_MAP[namedToken] !== undefined) return namedToken;

   return "";
}

function clampHue(value) {
   const numeric = Number(value);
   if (!Number.isFinite(numeric)) return DEFAULT_HUE;
   return Math.max(0, Math.min(360, Math.round(numeric)));
}

function clampColorIndex(value) {
   const numeric = Number(value);
   if (!Number.isFinite(numeric)) return DEFAULT_COLOR_INDEX;
   return Math.max(0, Math.min(PROFILE_COLOR_PALETTE.length - 1, Math.round(numeric)));
}

function colorIndexToCss(value) {
   return PROFILE_COLOR_PALETTE[clampColorIndex(value)]?.token || "--blue";
}

function rgbToHue(r, g, b) {
   const red = Math.max(0, Math.min(255, Number(r))) / 255;
   const green = Math.max(0, Math.min(255, Number(g))) / 255;
   const blue = Math.max(0, Math.min(255, Number(b))) / 255;

   const max = Math.max(red, green, blue);
   const min = Math.min(red, green, blue);
   const delta = max - min;

   if (delta === 0) return DEFAULT_HUE;

   let hue = 0;

   if (max === red) hue = ((green - blue) / delta) % 6;
   else if (max === green) hue = (blue - red) / delta + 2;
   else hue = (red - green) / delta + 4;

   const normalized = Math.round(hue * 60);
   return normalized < 0 ? normalized + 360 : normalized;
}

function extractHueFromColor(value, fallback = DEFAULT_HUE) {
   const raw = String(value || "").trim();
   if (!raw) return fallback;

   const token = normalizeHueToken(raw);
   if (token) return TOKEN_HUE_MAP[token];

   const hslMatch = raw.match(/^hsla?\(\s*([+-]?\d*\.?\d+)/i);
   if (hslMatch) return clampHue(hslMatch[1]);

   const hexMatch = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
   if (hexMatch) {
      const hex = hexMatch[1];
      const fullHex =
         hex.length === 3
            ? hex
                 .split("")
                 .map((char) => `${char}${char}`)
                 .join("")
            : hex;

      const red = parseInt(fullHex.slice(0, 2), 16);
      const green = parseInt(fullHex.slice(2, 4), 16);
      const blue = parseInt(fullHex.slice(4, 6), 16);
      return rgbToHue(red, green, blue);
   }

   const rgbMatch = raw.match(
      /^rgba?\(\s*(\d{1,3})\s*(?:,|\s)\s*(\d{1,3})\s*(?:,|\s)\s*(\d{1,3})/i,
   );
   if (rgbMatch) {
      return rgbToHue(rgbMatch[1], rgbMatch[2], rgbMatch[3]);
   }

   return fallback;
}

function colorIndexFromColor(value, fallbackIndex = DEFAULT_COLOR_INDEX) {
   const raw = String(value || "").trim();
   if (raw) {
      const exactIndex = PROFILE_COLOR_PALETTE.findIndex(
         (color) =>
            color.token.toLowerCase() === raw.toLowerCase() ||
            color.css.toLowerCase() === raw.toLowerCase(),
      );
      if (exactIndex >= 0) return exactIndex;
   }

   const fallbackHue = extractHueFromColor(
      PROFILE_COLOR_PALETTE[clampColorIndex(fallbackIndex)]?.token,
      DEFAULT_HUE,
   );
   const hue = clampHue(extractHueFromColor(value, fallbackHue));
   let bestIndex = clampColorIndex(fallbackIndex);
   let bestDistance = Number.POSITIVE_INFINITY;

   PROFILE_COLOR_PALETTE.forEach((color, index) => {
      const paletteHue = clampHue(extractHueFromColor(color.token, DEFAULT_HUE));
      const delta = Math.abs(hue - paletteHue);
      const distance = Math.min(delta, 360 - delta);

      if (distance < bestDistance) {
         bestDistance = distance;
         bestIndex = index;
      }
   });

   return bestIndex;
}

function normalizeItems(raw) {
   if (Array.isArray(raw)) return raw;
   if (!raw || typeof raw !== "object") return [];

   const candidates = [
      raw.items,
      raw.data,
      raw.results,
      raw.rows,
      raw.reservations,
      raw.list,
   ];

   for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
      if (candidate && typeof candidate === "object") {
         if (Array.isArray(candidate.items)) return candidate.items;
         if (Array.isArray(candidate.data)) return candidate.data;
         if (Array.isArray(candidate.results)) return candidate.results;
         if (Array.isArray(candidate.rows)) return candidate.rows;
         if (Array.isArray(candidate.reservations)) {
            return candidate.reservations;
         }
         if (Array.isArray(candidate.list)) return candidate.list;
      }
   }

   return [];
}

function extractServerErrors(error) {
   const raw = error?.message || error?.toString?.() || "";

   try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.message)) {
         return parsed.message
            .map((item) => String(item || "").trim())
            .filter(Boolean);
      }

      if (parsed?.message) {
         return [String(parsed.message).trim()].filter(Boolean);
      }
   } catch {}

   return [String(raw).trim()].filter(Boolean);
}

function normPlate(value) {
   return String(value || "")
      .replace(/\s+/g, "")
      .replace(/-/g, "")
      .toUpperCase();
}

function toApiGearbox(value) {
   return String(value || "")
      .toLowerCase()
      .includes("auto")
      ? "automat"
      : "manual";
}

function formatLocalDate(value) {
   if (!value) return "—";

   try {
      return new Intl.DateTimeFormat("ro-RO", {
         dateStyle: "short",
         timeZone: "Europe/Chisinau",
      }).format(new Date(value));
   } catch {
      return String(value);
   }
}

function pad2(value) {
   return String(value).padStart(2, "0");
}

function hhmmFromIso(value) {
   const date = new Date(value);
   return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
}

function serverDowFromLocalDate(localDateObj) {
   const y = localDateObj.getFullYear();
   const m = localDateObj.getMonth();
   const d = localDateObj.getDate();
   return new Date(Date.UTC(y, m, d)).getUTCDay();
}

function dowFromIsoUTC(value) {
   return new Date(value).getUTCDay();
}

function weekdayShortLabel(dow) {
   return WEEKDAY_OPTIONS.find((item) => item.key === dow)?.label || "Dum";
}

function toIsoUtcRaw(localDateObj, timeStrHHMM) {
   const [hh, mm] = String(timeStrHHMM || "00:00")
      .split(":")
      .map((item) => Number(item));

   return new Date(
      Date.UTC(
         localDateObj.getFullYear(),
         localDateObj.getMonth(),
         localDateObj.getDate(),
         hh || 0,
         mm || 0,
         0,
         0,
      ),
   ).toISOString();
}

function initWeeklySelection() {
   const map = new Map();
   for (const day of [0, 1, 2, 3, 4, 5, 6]) map.set(day, new Set());
   return map;
}

function extractBlackoutTitle(item) {
   const type = String(item?.type || "").toUpperCase();
   return type === "REPEAT" ? "Blocare repetitivă" : "Blocare";
}

const RATING_MONTH_LABELS = [
   "Ian",
   "Feb",
   "Mar",
   "Apr",
   "Mai",
   "Iun",
   "Iul",
   "Aug",
   "Sep",
   "Oct",
   "Nov",
   "Dec",
];

const RATING_MONTH_LABELS_FULL = [
   "Ianuarie",
   "Februarie",
   "Martie",
   "Aprilie",
   "Mai",
   "Iunie",
   "Iulie",
   "August",
   "Septembrie",
   "Octombrie",
   "Noiembrie",
   "Decembrie",
];

function formatRatingNumber(value) {
   const number = Number(value);
   if (!Number.isFinite(number)) return "—";
   return number % 1 === 0 ? String(number) : number.toFixed(1);
}

function getRatingTimestamp(rating) {
   const raw =
      rating?.reservation?.startTime ||
      rating?.createdAt ||
      rating?.updatedAt;
   if (!raw) return null;

   const date = new Date(raw);
   return Number.isNaN(date.getTime()) ? null : date;
}

function formatTrendMonthLabel(monthIndex, year) {
   const month = RATING_MONTH_LABELS[monthIndex] || "";
   if (!month) return String(year || "");
   return month;
}

const RATING_CHART_MIN = 1;
const RATING_CHART_MAX = 5;
const RATING_CHART_GOAL = 4.1;
const RATING_TREND_MONTHS = 6;
const RATINGS_MONTH_WINDOW = 5;
const RATINGS_MONTH_WINDOW_RADIUS = 2;

function getCurrentMonthSlot() {
   const now = new Date();
   return { year: now.getFullYear(), month: now.getMonth() };
}

function shiftMonthSlot({ year, month }, delta) {
   const date = new Date(year, month + delta, 1);
   return { year: date.getFullYear(), month: date.getMonth() };
}

function compareMonthSlots(left, right) {
   if (left.year !== right.year) return left.year - right.year;
   return left.month - right.month;
}

function monthSlotKey(slot) {
   return `${slot.year}-${slot.month}`;
}

function formatMonthTabLabel(slot) {
   const month = RATING_MONTH_LABELS[slot.month] || "";
   const yearSuffix = String(slot.year).slice(-2);
   return `${month} '${yearSuffix}`;
}

function buildVisibleMonthWindow(anchor) {
   const current = getCurrentMonthSlot();
   const isAtCurrent = compareMonthSlots(anchor, current) === 0;

   if (isAtCurrent) {
      return Array.from({ length: RATINGS_MONTH_WINDOW }, (_, index) =>
         shiftMonthSlot(current, index - (RATINGS_MONTH_WINDOW - 1)),
      );
   }

   const centered = Array.from({ length: RATINGS_MONTH_WINDOW }, (_, index) =>
      shiftMonthSlot(anchor, index - RATINGS_MONTH_WINDOW_RADIUS),
   );

   const withoutFuture = centered.filter(
      (slot) => compareMonthSlots(slot, current) <= 0,
   );

   if (withoutFuture.length >= RATINGS_MONTH_WINDOW) {
      return withoutFuture.slice(-RATINGS_MONTH_WINDOW);
   }

   const padded = [...withoutFuture];
   while (padded.length < RATINGS_MONTH_WINDOW) {
      const first = padded[0] || anchor;
      padded.unshift(shiftMonthSlot(first, -1));
   }

   return padded.slice(0, RATINGS_MONTH_WINDOW);
}

function ratingToChartPercent(value) {
   const numeric = Number(value);
   if (!Number.isFinite(numeric)) return 0;
   const clamped = Math.max(
      RATING_CHART_MIN,
      Math.min(RATING_CHART_MAX, numeric),
   );
   return (
      ((clamped - RATING_CHART_MIN) / (RATING_CHART_MAX - RATING_CHART_MIN)) *
      100
   );
}

function ratingBarHeight(average) {
   if (average == null) return 10;
   return Math.max(12, Math.min(100, ratingToChartPercent(average)));
}

function buildTrendBuckets(ratings, anchor) {
   const current = getCurrentMonthSlot();
   const monthSlots = Array.from({ length: RATING_TREND_MONTHS }, (_, index) =>
      shiftMonthSlot(anchor, index - (RATING_TREND_MONTHS - 1)),
   );

   return monthSlots.map(({ year: slotYear, month }) => {
      const monthStart = new Date(slotYear, month, 1);
      const monthEnd = new Date(slotYear, month + 1, 0, 23, 59, 59, 999);
      const isFuture =
         slotYear > current.year ||
         (slotYear === current.year && month > current.month);
      const isCurrent =
         slotYear === current.year && month === current.month;
      const isSelected =
         slotYear === anchor.year && month === anchor.month;

      const inBucket = (Array.isArray(ratings) ? ratings : []).filter(
         (rating) => {
            const timestamp = getRatingTimestamp(rating);
            if (!timestamp) return false;
            return timestamp >= monthStart && timestamp <= monthEnd;
         },
      );

      const average =
         inBucket.length > 0
            ? inBucket.reduce(
                 (sum, rating) => sum + Number(rating?.rating || 0),
                 0,
              ) / inBucket.length
            : null;

      return {
         key: `${slotYear}-${month}`,
         label: formatTrendMonthLabel(month, slotYear),
         average: isFuture ? null : average,
         count: inBucket.length,
         isFuture,
         isCurrent,
         isSelected,
      };
   });
}

function getTrendBarClass(average) {
   if (average == null) return "is-empty";
   return "is-rated";
}

function getTrendBarStyle(average, animationIndex = 0) {
   if (average == null) return undefined;

   return {
      "--stats-trend-bar-height": `${ratingBarHeight(average)}%`,
      background: getTrendBarFill(average) || undefined,
      animationDelay: `${Math.max(0, animationIndex) * 0.07}s`,
   };
}

function filterRatingsByPeriod(
   ratings,
   { year, month = null, fullYear = false } = {},
) {
   const safeYear = Number(year);
   if (!Number.isFinite(safeYear)) return [];

   return (Array.isArray(ratings) ? ratings : []).filter((rating) => {
      const timestamp = getRatingTimestamp(rating);
      if (!timestamp) return false;

      if (fullYear) {
         return timestamp.getFullYear() === safeYear;
      }

      if (!Number.isInteger(month)) return false;

      const monthStart = new Date(safeYear, month, 1);
      const monthEnd = new Date(safeYear, month + 1, 0, 23, 59, 59, 999);
      return timestamp >= monthStart && timestamp <= monthEnd;
   });
}

function computeRatingStats(ratings) {
   const values = (Array.isArray(ratings) ? ratings : [])
      .map((rating) => Number(rating?.rating))
      .filter((value) => Number.isFinite(value));

   if (!values.length) {
      return {
         average: null,
         total: 0,
         min: null,
         max: null,
      };
   }

   const sum = values.reduce((acc, value) => acc + value, 0);
   return {
      average: sum / values.length,
      total: values.length,
      min: Math.min(...values),
      max: Math.max(...values),
   };
}

function formatStatsPeriodLabel({ year, month, fullYear }) {
   if (fullYear) return `Anul ${year}`;
   const monthLabel =
      RATING_MONTH_LABELS_FULL[month] || RATING_MONTH_LABELS[month] || "";
   return monthLabel ? `${monthLabel} ${year}` : String(year || "");
}

function readBlackoutCreator(item) {
   const source = item?.createdBy || item?.creator || item?.user || {};
   const first = String(source.firstName || item?.createdByFirstName || "").trim();
   const last = String(source.lastName || item?.createdByLastName || "").trim();
   const full = `${first} ${last}`.trim();
   return (
      full ||
      String(source.name || item?.createdByName || item?.authorName || "").trim()
   );
}
export {
   TABS,
   TOKEN_HUE_MAP,
   DEFAULT_HUE,
   AVATAR_COLORS,
   SLOT_OPTIONS,
   WEEKDAY_OPTIONS,
   PROFILE_COLOR_PALETTE_TOKENS,
   PROFILE_COLOR_PALETTE,
   DEFAULT_COLOR_INDEX,
   EDITABLE_INSTRUCTOR_TYPE_OPTIONS,
   firstLetter,
   getInitials,
   hashStringToUInt,
   getAvatarColorFromName,
   normalizeColorToCss,
   normalizeHueToken,
   clampHue,
   clampColorIndex,
   colorIndexToCss,
   rgbToHue,
   extractHueFromColor,
   colorIndexFromColor,
   normalizeItems,
   extractServerErrors,
   normPlate,
   toApiGearbox,
   formatLocalDate,
   pad2,
   hhmmFromIso,
   serverDowFromLocalDate,
   dowFromIsoUTC,
   weekdayShortLabel,
   toIsoUtcRaw,
   initWeeklySelection,
   extractBlackoutTitle,
   RATING_MONTH_LABELS,
   RATING_MONTH_LABELS_FULL,
   formatRatingNumber,
   getRatingTimestamp,
   formatTrendMonthLabel,
   RATING_CHART_MIN,
   RATING_CHART_MAX,
   RATING_CHART_GOAL,
   RATING_TREND_MONTHS,
   RATINGS_MONTH_WINDOW,
   RATINGS_MONTH_WINDOW_RADIUS,
   getCurrentMonthSlot,
   shiftMonthSlot,
   compareMonthSlots,
   monthSlotKey,
   formatMonthTabLabel,
   buildVisibleMonthWindow,
   ratingToChartPercent,
   ratingBarHeight,
   buildTrendBuckets,
   getTrendBarClass,
   getTrendBarStyle,
   filterRatingsByPeriod,
   computeRatingStats,
   formatStatsPeriodLabel,
   readBlackoutCreator,
};
